// ─── Espaces (Core) ───────────────────────────────────────────────
// L'ESPACE est le TENANT de la suite : conteneur des données d'un foyer/compte,
// auquel chaque module (inventaire, budget…) se rattache, et qui portera les
// DROITS D'ÉCHANGE entre apps (quelle app lit/écrit quel module).
//   espaces/{id} {
//     nom, emoji, membres:{uid:role}, membreUids:[uid],
//     droits?:{ [appId]: { [moduleId]: 'read'|'write' } },   // extensible
//     tenantsHerites?:[id],   // tenants historiques adoptés (cf. heritage.js)
//     creePar, creeParNom, creeLe
//   }
import {
  collection, doc, addDoc, updateDoc, deleteDoc, onSnapshot, query, where,
  arrayUnion, arrayRemove, deleteField,
} from 'firebase/firestore';
import { db } from '../firebase';

const emailNorm = (e) => (e || '').trim().toLowerCase();

// Espaces dont l'utilisateur est membre (temps réel), triés par date de création.
export function ecouterMesEspaces(uid, callback) {
  if (!uid) { callback([]); return () => {}; }
  const q = query(collection(db, 'espaces'), where('membreUids', 'array-contains', uid));
  return onSnapshot(
    q,
    (snap) => {
      const liste = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // String() défensif : un creeLe non-chaîne (ex. Timestamp) ferait planter
      // localeCompare et viderait TOUTE la liste (cf. incident ivv-suite).
      liste.sort((a, b) => String(a.creeLe || '').localeCompare(String(b.creeLe || '')));
      callback(liste);
    },
    () => callback([]),
  );
}

export async function creerEspace({ nom, emoji, uid, nomCreateur, emailCreateur }) {
  const n = (nom || '').trim() || 'Mon espace';
  const ref = await addDoc(collection(db, 'espaces'), {
    nom: n,
    emoji: emoji || '🏠',
    membres: uid ? { [uid]: 'proprietaire' } : {},
    membreUids: uid ? [uid] : [],
    noms: uid ? { [uid]: nomCreateur || '' } : {},   // libellé d'affichage par uid
    emails: uid ? { [uid]: emailNorm(emailCreateur) } : {},   // idem, pour distinguer des homonymes
    invitations: [],                                  // e-mails invités, pas encore membres
    acces: {},                                        // accès déclaré (finance, cal:<id>)
    droits: {},
    creePar: uid || null,
    creeParNom: nomCreateur || '',
    creeLe: new Date().toISOString(),
  });
  return ref.id;
}

export async function renommerEspace(id, nom, emoji) {
  const patch = { nom: (nom || '').trim() || 'Mon espace' };
  if (emoji) patch.emoji = emoji;
  await updateDoc(doc(db, 'espaces', id), patch);
}

export async function supprimerEspace(id) {
  await deleteDoc(doc(db, 'espaces', id));
}

// ── Tenants hérités (pont de migration, cf. espace/heritage.js) ───
// `tenantsHerites` : identifiants historiques (calendriers MoPCal) dont cet
// espace reprend les données. Les modules lisent sur l'union.
export async function rattacherTenant(espaceId, tenantId) {
  if (!espaceId || !tenantId) return;
  await updateDoc(doc(db, 'espaces', espaceId), { tenantsHerites: arrayUnion(tenantId) });
}

export async function detacherTenant(espaceId, tenantId) {
  if (!espaceId || !tenantId) return;
  await updateDoc(doc(db, 'espaces', espaceId), { tenantsHerites: arrayRemove(tenantId) });
}

// Rôle d'un uid dans un espace (proprietaire | membre | null).
export function roleDansEspace(espace, uid) {
  return (espace && espace.membres && espace.membres[uid]) || null;
}

// ── Membres : invitation par e-mail, adhésion, retrait, rôle ──────
// L'utilisateur devient membre quand il OUVRE l'app avec un e-mail invité
// (rejoindreEspace, déclenché par EspaceContext). Avant ça, il figure dans
// `invitations` (affiché « en attente »).
export async function inviterMembre(espaceId, email) {
  const e = emailNorm(email);
  if (!espaceId || !e) return;
  await updateDoc(doc(db, 'espaces', espaceId), { invitations: arrayUnion(e) });
}

export async function annulerInvitation(espaceId, email) {
  await updateDoc(doc(db, 'espaces', espaceId), { invitations: arrayRemove(emailNorm(email)) });
}

// Espaces où MON e-mail est invité mais où je ne suis pas encore membre.
export function ecouterEspacesInvites(email, callback) {
  const e = emailNorm(email);
  if (!e) { callback([]); return () => {}; }
  const q = query(collection(db, 'espaces'), where('invitations', 'array-contains', e));
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    () => callback([]),
  );
}

export async function rejoindreEspace(espaceId, { uid, email, prenom }) {
  if (!espaceId || !uid) return;
  await updateDoc(doc(db, 'espaces', espaceId), {
    [`membres.${uid}`]: 'membre',
    [`noms.${uid}`]:    prenom || emailNorm(email) || '',
    [`emails.${uid}`]:  emailNorm(email),
    membreUids:  arrayUnion(uid),
    invitations: arrayRemove(emailNorm(email)),
  });
}

export async function retirerMembre(espaceId, uid) {
  if (!espaceId || !uid) return;
  await updateDoc(doc(db, 'espaces', espaceId), {
    [`membres.${uid}`]: deleteField(),
    [`noms.${uid}`]:    deleteField(),
    [`emails.${uid}`]:  deleteField(),
    membreUids: arrayRemove(uid),
  });
}

export async function definirRole(espaceId, uid, role) {
  if (!espaceId || !uid) return;
  await updateDoc(doc(db, 'espaces', espaceId), { [`membres.${uid}`]: role });
}

// Met à jour le libellé d'affichage (prénom) d'un membre dans l'espace. Sert à
// l'auto-réparation : le doc espace porte une COPIE dénormalisée `noms` (uid →
// prénom) pour afficher les membres sans requête ; on la rafraîchit quand elle
// diverge du prénom courant du compte.
export async function majNomMembre(espaceId, uid, nom) {
  if (!espaceId || !uid) return;
  await updateDoc(doc(db, 'espaces', espaceId), { [`noms.${uid}`]: nom || '' });
}

// Même patron que `majNomMembre`, pour l'e-mail : les espaces créés avant cet
// incrément n'ont pas de `emails[uid]` — l'auto-réparation le pose au premier
// passage de chaque membre, sans script de rattrapage.
export async function majEmailMembre(espaceId, uid, email) {
  if (!espaceId || !uid) return;
  await updateDoc(doc(db, 'espaces', espaceId), { [`emails.${uid}`]: emailNorm(email) });
}

// ── Accès déclaré d'une ressource « externe » (finance, calendrier) ─
// `cle` : 'finance' ou 'cal:<calendrierId>'. `lecteurs` : liste d'uids, ou null
// pour revenir à « tout le monde ».
export async function definirAccesDeclare(espaceId, cle, lecteurs) {
  if (!espaceId || !cle) return;
  await updateDoc(doc(db, 'espaces', espaceId), {
    [`acces.${cle}`]: Array.isArray(lecteurs) ? lecteurs : deleteField(),
  });
}

// Variante UNIFIÉE (grammaire 3 niveaux) : stocke la carte dans `accesN.{cle}`
// et maintient `acces.{cle}` (liste de lecteurs, null = tout le monde) en
// miroir rétro-compat pour les modules qui lisent encore l'ancien format.
export async function definirAccesDeclareN(espaceId, cle, accesN) {
  if (!espaceId || !cle) return;
  const a = accesN || {};
  const tous = !!a['*'];
  const nominatifs = Object.keys(a).filter(k => k !== '*');
  await updateDoc(doc(db, 'espaces', espaceId), {
    [`accesN.${cle}`]: a,
    [`acces.${cle}`]: tous ? deleteField() : nominatifs,
  });
}
// Carte des niveaux déclarée : `accesN.{cle}` si présent, sinon depuis l'ancienne
// liste. Rien de déclaré → PRIVÉ (seul le propriétaire) : la sécurité par défaut
// est « personne ne voit », on ouvre ressource par ressource.
export function accesDeclareN(espace, cle) {
  const n = espace?.accesN?.[cle];
  if (n && typeof n === 'object' && !Array.isArray(n)) return n;
  const lecteurs = espace?.acces?.[cle];
  if (!Array.isArray(lecteurs)) return {};
  const out = {};
  lecteurs.forEach(uid => { out[uid] = 'lecteur'; });
  return out;
}
